import React, {useContext, useEffect, useState, useRef, RefObject} from 'react';
import html2canvas from "html2canvas";
import {LinearProgress} from "@material-ui/core";
import {pdf} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import BaseModal from "../../Components/Modals/BaseModal";
import TextInput from "../../Containers/TextInput/TextInput";
import ResultReportContainer from '../ResultReports/ResultReportContainer';
import resultsContext from '../../Foundations/Contexts/ResultsContext';
import searchFilterContext from '../../Foundations/Contexts/SearchFilterContext';
import ReportMapView from '../../Containers/MapView/ReportMapView';
import {MAX_ALLOWED_RESULT} from '../../Foundations/Constants/ReportConfig';
import {IResultsData} from '../../Foundations/Types/IResultsData';
import {saveReportToAWS, storeReportFilter} from '../../Foundations/API/SearchApi';
import appContext from '../../Foundations/Contexts/AppContext';
import {IGenericObject} from '../../Foundations/Types/IGenericObject';

interface IProps {
    hide(): void;
}

enum PDF_STATE {
    IDLE = 'idle',
    PROCESSING = 'processing',
    DONE = 'done'
}

const ExportReportModal: React.FC<IProps> = ({hide}) => {
    const { user, env } = useContext(appContext);
    const { results, ref, resultSummary, depotsAtArea, suppliersAtArea, selectedRadius } = useContext(resultsContext);
    const { searchParameters, availableFilters, activeFilters } = useContext(searchFilterContext);
    const [filename, setFilename] = useState<string>('');
    const [initPdf, setInitPdf] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pdfReportState, setPdfReportState] = useState<PDF_STATE>(PDF_STATE.IDLE);
    const [mapSnapShot, setMapSnapShot] = useState<string>('');
    const [isSnapshotReady, setIsSnapshotReady] = useState<boolean>(false);
    const [selectedFilter, setSelectedFilter] = useState<IGenericObject | null>(null);
    const mapRef: RefObject<HTMLDivElement> | null = useRef(null);

    useEffect(() => {
       if (isLoading === true && initPdf === false && isSnapshotReady === true) {
           storeReportFilter(ref, activeFilters)
               .then((res) => {
                   setSelectedFilter(res);
                   setInitPdf(true);
               })
               .catch(() => {
                   // @Todo handle error
                   setInitPdf(true);
               })
       }
    }, [isSnapshotReady, isLoading, initPdf, availableFilters, activeFilters, ref, env])

    const handleChange = (value: string) => {
        setFilename(`${value}`)
    }

    const handleExportPDFClick = (): void => {
        createMapSnapShot();
    }

    const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key.toLowerCase() === 'enter') {
            createMapSnapShot();
        }
    }

    const createMapSnapShot = (): void => {
        setIsLoading(true);
        setFilename(`${filename}${filename !== '' ? '-' : ''}${ref}`);

        if (mapRef.current === null) {
            setIsSnapshotReady(true);
            return;
        }

        html2canvas(mapRef.current, {
            useCORS: true,
            allowTaint: false,
            width: 794,
            height: 300,
            scale: 1,
            scrollX: 0,
            scrollY: 0
        }).then((canvas) => {
            const img = canvas.toDataURL('image/png');
            setIsSnapshotReady(true);
            setMapSnapShot(img);
        });
    }

    const limitResult = (): IResultsData[] => {
        return results.length > MAX_ALLOWED_RESULT
            ? results.splice(0, MAX_ALLOWED_RESULT)
            : results
    }

    const handleCLick = async (resultSummary) => {
        if (pdfReportState !== PDF_STATE.IDLE) {
            return;
        }

        setPdfReportState(PDF_STATE.PROCESSING);

        const doc = <ResultReportContainer
            selectedFilter={selectedFilter}
            mapSnapShot={mapSnapShot}
            resultSummary={resultSummary}
            results={limitResult()}
            searchParameters={searchParameters}
            availableFilters={availableFilters}
            activeFilters={activeFilters}
            searchRef={filename}
            depotsAtArea={depotsAtArea}
            suppliersAtArea={suppliersAtArea}
            radius={selectedRadius}
        />;
        // @ts-ignore
        const asPdf = pdf([]); // {} is important, throws without an argument
        asPdf.updateContainer(doc);
        const blob = await asPdf.toBlob();
        saveAs(blob, `${filename}.pdf`);
        saveReportToAWS(user.token, blob, `${filename}.pdf`);
        setPdfReportState(PDF_STATE.DONE);
        setTimeout(() => {
            hide();
        }, 500);
    }

    const renderContent = () => (
        <div>
            <div className="overflow-hidden w-0 h-0 fixed left-0 top-0">
                <div ref={mapRef} style={{height: '300px', width: '794px', marginLeft: '-15px'}} className="flex">
                    <ReportMapView />
                </div>
            </div>
            <div className="pt-2 pb-4 text-sm leading-6">
                Enter your chosen file name otherwise a default name will be chosen for you.
                {
                    results.length > MAX_ALLOWED_RESULT &&
                    <div className="text-xs text-red m-2">
                        It will take some time to exporting large results, please wait and do not close
                        this page while report is processed.
                    </div>
                }
            </div>
            <TextInput
                label="Reference Number"
                error=''
                value={filename}
                handleBlur={() => null}
                handleChange={handleChange}
                placeholder="Filename"
                onKeyPress={handleEnter}
                disabled={initPdf}
            />
            <div className="relative py-5">
                {
                    initPdf === true &&
                    resultSummary !== null
                    ? <button
                        disabled={pdfReportState !== PDF_STATE.IDLE}
                        className="h-10 text-sm font-bold w-full rounded-md bg-blue text-white justify-center flex items-center cursor-pointer absolute top-2 z-10"
                        onClick={() => handleCLick(resultSummary)}
                    >
                        { pdfReportState === PDF_STATE.IDLE && 'Download report'}
                        { pdfReportState === PDF_STATE.PROCESSING && 'Processing...'}
                        { pdfReportState === PDF_STATE.DONE && 'Done'}
                    </button>
                    : <button
                        disabled={isLoading}
                        className="h-10 text-sm font-bold w-full rounded-md bg-blue text-white justify-center flex items-center cursor-pointer disabled:opacity-75"
                        onClick={handleExportPDFClick}
                    >Export PDF</button>
                }
                {
                    isLoading === true &&
                    <LinearProgress className="py-1 mt-3 rounded-xl"/>
                }
            </div>
        </div>
    )
    return <BaseModal content={renderContent()} header="Export report" hide={hide}/>
}

export default ExportReportModal
